// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Step } from "@guestsupply/App/Widgets/Stepper/helpers";
import translate from "@insite/client-framework/Translate";
import { NamedExoticComponent } from "react";

export const constants = {
    header: {
        size: {
            sticky: 90,
        },
        mobile: {
            size: {
                height: 156,
            },
        },
        tablet: {
            size: {
                height: 105,
            },
        },
    },
    footer: {
        offsetTop: 64,
    },
    searchInput: {
        breakpoints: {
            lg: 1280,
        },
    },
    cartLine: {
        breakpoints: {
            md: 1200,
            xs: "xs",
        },
    },
    productList: {
        xSortAndFilter: {
            screen: "isTabletScreen",
            breakpoint: "md",
        },
    },
    productGridView: {
        desktop: {
            width: 308,
            height: 308,
        },
        tablet: {
            width: 346,
            height: 230,
        },
        mobile: {
            width: 328,
            height: 280,
        },
    },
};

export const XNGAGE_CUSTOM_WIDGET = "Xngage Custom" as any;
export const EMPTY_VALUE = "- -";
export const DivWrapper = `div[class^="Wrapper"]`;
export const CartLinkZoneName = "Cart";
export const SignInZoneName = "SignIn";

interface AddToCartStatusInfoInterface {
    status: string;
    label: string;
    icon: NamedExoticComponent<{}> | string;
}
interface AddToCartStatusInterface {
    default: AddToCartStatusInfoInterface;
    loading: AddToCartStatusInfoInterface;
    success: AddToCartStatusInfoInterface;
}

export const addToCartStatus: AddToCartStatusInterface = {
    default: {
        status: "default",
        label: "Add to Cart",
        icon: "",
    },
    loading: {
        status: "loading",
        label: "",
        icon: "",
    },
    success: {
        status: "success",
        label: "Added",
        icon: "",
    },
};

export const SLIDES_PER_VIEW_POINTS = {
    xs: 0,
    sm: 1,
    md: 2,
    lg: 3,
};

export const checkoutPages = ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"];

export const sharedDateTimeOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
};

export enum USER_ROLES {
    ADMIN = "Administrator",
    BUYER1 = "Buyer1",
    BUYER2 = "Buyer2",
    BUYER3 = "Buyer3",
    FINANCE = "Finance",
    REQUISITIONER = "Requisitioner",
    PUNCHOUT = "PunchOut",
}

export enum fontFamilyValues {
    BODONI = "'Bodoni MT', 'Gotham', 'Roboto', 'Lato', sans-serif",
    GOTHAM = "'Gotham', 'Roboto', 'Lato', sans-serif",
}

export const customXngageWidget = "Xngage Custom" as any;

export const shippingWeightUnit = " lbs";

export const brandStandards = {
    brandMandate: "Brand Mandate",
    meetsBrandStandards: "Meets Brand Standard",
    guestSupplyRecommended: "Guest Supply Recommended",
    brandApprovedSubstitute: "Brand Approved Substitute",
    brandPreferredProduct: "Brand Preferred Product",
};

export const CHECKOUT_STEPS = {
    Addresses: 1,
    ReviewAndPay: 2,
};

export const checkoutSteps: Step[] = [
    {
        index: CHECKOUT_STEPS.Addresses,
        name: `${translate("Customer & Shipping Address")}`,
        prev: -1,
        displayName: `${translate("Customer & Shipping Address")}`,
        next: CHECKOUT_STEPS.Addresses,
    },
    {
        index: CHECKOUT_STEPS.ReviewAndPay,
        name: `${translate("Review & Confirm Order")}`,
        prev: CHECKOUT_STEPS.Addresses,
        displayName: `${translate("Review & Confirm Order")}`,
    },
];

export const checkoutTitles = {
    [CHECKOUT_STEPS.Addresses]: {
        auth: translate("Customer & Shipping Address"),
        unAuth: translate("Shipping Address"),
    },
    [CHECKOUT_STEPS.ReviewAndPay]: {
        auth: translate("Review & Pay"),
        unAuth: translate("Review & Pay"),
    },
};

export const poRegex = /^[a-zA-Z0-9\s]*$/;

export const GOOGLE_EVENTS = {
    MY_ACCOUNT_RECENT_ORDERS: "myacct_recentorders",
    MY_ACCOUNT_INVOICE_HISTORY: "myacct_invoicehistory",
    MY_ACCOUNT_ORDER_APPROVAL: "myacct_orderapproval",
    MY_ACCOUNT_REQUISITIONS: "myacct_requisitions",
    MY_ACCOUNT_MY_LISTS: "myacct_mylists",
    MY_ACCOUNT_QUICK_LINKS: "myacct_quicklinks",
    PLP_SEARCH: "plp_search",
    PLP_FILTER: "plp_filter",
    PAYMENT_PORTAL: "payment_portal",
    SHARE_PRODUCT: "share_product",
    RMA_REQUEST: "rma_request",
    BM_CANCEL_PRODUCTS: "bm_cancel_products",
    BM_Alternate_DC: "alternate_dc",
    REORDER: "reorder",
    ORDER_HISTORY: "order_history",
    AUTO_DELIVERY: "autodelivery",
    LIST_SCHEDULE_REMINDER: "lists_schedule_reminder",
    LOGIN: "login",
    REQUEST_ACCESS: "request_access",
    NAVIGATION_BAR: "nav_bar",
    TOOL_BAR_NAVIGATION: "tool_bar_nav",
    MY_ACCOUNT_SHORTCUT_EVENT: "my_acct_shortcuts",
    POPULAR_CATEGORIES: "popular_categories",
    FEATURED_ITEMS: "featured_items",
    FEATURED_PARTNERS: "featured_partners",
    OUR_SOLUTIONS: "our_solutions",
    SOLUTIONS_DESK_BLOG: "solutions_desk_blog",
    MY_ACCOUNT_RECENT_ORDERS_HOME_PAGE: "recent_orders_homepg",
    HOME_PAGE_SLIDER: "homepg_slider",
    MY_ACCOUNT_FLYOUT: "myacct_flyout",
    MY_ORDER_GUIDE_CATEGORY: "myorderguide_category",
    MY_ORDER_GUIDE_SEARCH: "myorderguide_search",
    SEARCH_RESULT: "searchResults",
    AUTOCOMPLETE: "autocomplete",
    VIEW_ITEM_LIST: "view_item_list",
    SELECT_ITEM: "select_item",
    VIEW_CART: "view_cart",
    ADD_TO_WISHLIST: "add_to_wishlist",
    REMOVE_FROM_CART: "remove_from_cart",
    ADD_TO_CART: "add_to_cart",
    UPDATE_CART: "edit_cart",
    MY_ACCOUNT_QUICK_ORDER: "myacct_quickorder",
    BEGIN_CHECKOUT: "begin_checkout",
    ADD_SHIPPING_INFO: "add_shipping_info",
    ADD_PAYMENT_INFO: "add_payment_info",
    PURCHASE: "purchase",
    SELECT_PROMOTION: "select_promotion",
    VIEW_PROMOTION: "view_promotion",
    REGISTRATION: "registration",
    VIEW_ITEM: "view_item",
    CUSTOM_VIRTUAL_PAGE_VIEW: "custom_virtualPageView",
    PUNCHOUT_CHECKOUT: "punchout_checkout",
    CANCEL_PUNCHOUT_CART: "cancel_punchout_cart",
    NEW_AUTO_DELIVERY: "new_autodelivery",
};

export enum XngCustomPdfLayoutOptions {
    GuestSupplyPrint = "GuestSupplyPrint",
    Standard = "Standard",
    Avery94224 = "Avery94224",
    Avery22806 = "Avery22806",
}
